import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, errMsg, fmt } from "@/lib/api";

export function PaymentCancelled() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-5" data-testid="payment-cancelled-page">
      <div className="text-center max-w-md">
        <span className="inline-flex w-16 h-16 rounded-full bg-kay-rose items-center justify-center mb-6">
          <XCircle className="text-kay-pink" size={30} />
        </span>
        <h1 className="font-display text-4xl leading-tight">Paiement <span className="italic text-kay-pink">annulé.</span></h1>
        <p className="mt-4 text-sm text-kay-ink/60 leading-relaxed">
          Aucun montant n'a été débité. Ta commande est enregistrée en attente — tu peux reprendre ton paiement
          ou nous écrire sur Snapchat si tu préfères un virement.
        </p>
        <div className="mt-8 flex gap-3 justify-center flex-wrap">
          <Link to="/compte" data-testid="payment-cancelled-account" className="rounded-full bg-kay-ink text-white px-7 py-3 text-sm font-semibold hover:bg-kay-pink transition-colors">
            Voir mes commandes
          </Link>
          <Link to="/boutique" className="rounded-full border border-kay-line px-7 py-3 text-sm font-semibold hover:border-kay-ink transition-colors">
            Retour boutique
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true });
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const token = params.get("token") || "";
    api
      .get("/paypal/return", { params: { token } })
      .then((r) => setState({ loading: false, ok: true, ...r.data }))
      .catch((e) => setState({ loading: false, ok: false, error: errMsg(e) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-5" data-testid="payment-return-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
        {state.loading ? (
          <>
            <Loader2 className="mx-auto animate-spin text-kay-pink" size={34} />
            <p className="mt-6 font-display italic text-2xl text-kay-ink/60" data-testid="payment-return-loading">Vérification de ton paiement…</p>
          </>
        ) : state.ok ? (
          <>
            <span className="inline-flex w-16 h-16 rounded-full bg-kay-rose items-center justify-center mb-6">
              <CheckCircle2 className="text-kay-green" size={30} />
            </span>
            <h1 className="font-display text-4xl leading-tight" data-testid="payment-return-success">
              Paiement <span className="italic text-kay-pink">confirmé !</span>
            </h1>
            <p className="mt-4 text-sm text-kay-ink/60" data-testid="payment-return-details">
              Commande <strong className="font-mono">{state.order_number}</strong> — {fmt(state.total)} reçus.
              Un email de confirmation vient de partir, ta commande passe en préparation.
            </p>
            <Link to="/compte" data-testid="payment-return-account" className="mt-8 inline-block rounded-full bg-kay-ink text-white px-8 py-3.5 text-sm font-semibold hover:bg-kay-pink transition-colors">
              Suivre ma commande
            </Link>
          </>
        ) : (
          <>
            <span className="inline-flex w-16 h-16 rounded-full bg-kay-rose items-center justify-center mb-6">
              <XCircle className="text-kay-pink" size={30} />
            </span>
            <h1 className="font-display text-4xl leading-tight" data-testid="payment-return-error">Un souci de <span className="italic text-kay-pink">paiement.</span></h1>
            <p className="mt-4 text-sm text-kay-ink/60">{state.error}</p>
            <Link to="/panier" className="mt-8 inline-block rounded-full bg-kay-ink text-white px-8 py-3.5 text-sm font-semibold hover:bg-kay-pink transition-colors">
              Retour au panier
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
